// Pure URL-parsing logic for ActivateAccountPage.js -- separated out so it
// is directly unit-testable without a DOM/React rendering harness, same
// convention as this project's other admin-panel/src/*.test.js files
// (adminAccess.js, orderLocation.js, etc: pure logic next to the component
// that uses it).

export const SUPPORTED_ACTIVATION_LANGS = ['az', 'ru', 'en'];

/** Defaults to 'az' for a missing/unrecognized value, this app's established default. */
export function normalizeActivationLang(value) {
  const lang = String(value || '').trim().toLowerCase();
  return SUPPORTED_ACTIVATION_LANGS.includes(lang) ? lang : 'az';
}

/**
 * Determines which landing state /activate should show and which language
 * to render it in, from the page's own URL. Pure -- no DOM/window access --
 * so the caller passes window.location.hash/search in directly.
 *
 * TWO activation-link mechanisms can reach this page:
 *
 *   - CURRENT (functions/index.js's sendAccountActivationEmail, via
 *     Firebase's generateEmailVerificationLink with handleCodeInApp:
 *     false): Firebase's OWN hosted action page consumes the oobCode
 *     itself and only ever shows a "Continue" link to this page's URL on
 *     SUCCESS -- never on an invalid/expired/already-used code (Firebase's
 *     own page shows its own error in that case and does not link here at
 *     all). No token/oobCode of ours ever appears in this page's URL for
 *     this mechanism. Reaching this page with none is therefore ITSELF
 *     the success signal, not a missing/invalid one.
 *   - LEGACY (backward compatibility with links already issued before
 *     that migration): a PakMazar-issued opaque token in the URL HASH
 *     fragment (deliberately never the query string, and never sent to
 *     any server log), consumed by POSTing to the activateAccount Cloud
 *     Function -- unchanged, still fully supported.
 *
 * @param {string} hash window.location.hash, e.g. "#token=..."
 * @param {string} search window.location.search, e.g. "?lang=az"
 * @return {{kind: 'legacy-token', token: string, lang: string}
 *        | {kind: 'likely-verified', lang: string}}
 */
export function resolveActivationLandingState(hash, search) {
  const fragment = String(hash || '').startsWith('#')
    ? String(hash).slice(1)
    : '';
  const hashParams = new URLSearchParams(fragment);
  const token = String(hashParams.get('token') || '').trim();

  const queryParams = new URLSearchParams(search || '');
  const lang = normalizeActivationLang(queryParams.get('lang'));

  // Same bound the activateAccount Cloud Function itself already enforces
  // server-side -- mirrored here only to route to the right UI state,
  // never trusted as the actual validity check (the Cloud Function
  // re-validates the token fully against Firestore).
  if (token.length >= 40 && token.length <= 128) {
    return {kind: 'legacy-token', token, lang};
  }

  return {kind: 'likely-verified', lang};
}
